import { DeliveryAddressVerificationBadge } from '@/components/orders/DeliveryAddressVerificationBadge'
import {
  formatDeliveryRouteDate,
  getDeliveryRouteLocationLabel,
  getDeliveryRouteLocationMismatchMessage,
  getDeliveryRouteOrderAddress,
  getDeliveryRouteOrderLabel,
  getDeliveryRouteSelectionId,
  getSafeDayjs,
  isDeliveryShareSelectable,
} from '@/components/orders/delivery-share.utils'
import DeliveryRouteOrderLink from '@/components/orders/DeliveryRouteOrderLink'
import {
  DeliveryRouteOrderItemsList,
  getDeliveryRouteOrderItemCount,
  getDeliveryRouteOrderItems,
} from '@/components/orders/DeliveryRouteOrderItemsDisclosure'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Typography } from '@/components/ui/typography'
import dayjs from '@/lib/dayjs'
import { salesOrderFixAddressSearch } from '@/lib/services/sales-orders/sales-order.schema'
import { cn } from '@/lib/utils'
import { ChevronDown, Lock, Pencil, ShoppingBasket, Trash2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { isDeliveryAddressExplicitlyUnverified } from './delivery-address-verification'

export function isSelectableDeliveryRouteOrderPastDue(order, today = dayjs().format('YYYY-MM-DD')) {
  const deliveryDate = getSafeDayjs(order?.fulfillment_due_date)
  const todayDate = getSafeDayjs(today) ?? dayjs()

  return Boolean(deliveryDate?.isBefore(todayDate, 'day'))
}

function SelectableDeliveryRouteOrderRow({
  anchorOrder,
  isSelected,
  onToggle,
  order,
  routeStopNumber = null,
  blockedDescription = 'Verify this delivery address before adding it to a route.',
  isLocked = false,
  isMapHighlighted = false,
  isReadOnly = false,
  isSelectableOverride = null,
  onEditManualDelivery,
  onRemoveManualDelivery,
  onHoverChange,
  scrollIntoViewOnMapHighlight = false,
}) {
  const cardRef = useRef(null)
  const [isItemsOpen, setIsItemsOpen] = useState(false)
  const isAddressBlocked =
    !isLocked && isDeliveryAddressExplicitlyUnverified(order.delivery_address)
  const isSelectableByDefault = isDeliveryShareSelectable(order, anchorOrder)
  const isSelectable =
    !isAddressBlocked && (isSelected || (isSelectableOverride ?? isSelectableByDefault))
  const isDisabledForLocation = Boolean(anchorOrder) && !isAddressBlocked && !isSelectable
  const locationMismatchDescription = isDisabledForLocation
    ? getDeliveryRouteLocationMismatchMessage(order, anchorOrder)
    : null
  const isSelectionDisabled =
    isReadOnly || isLocked || isDisabledForLocation || (isAddressBlocked && !isSelected)
  const selectionId = getDeliveryRouteSelectionId(order)
  const checkboxId = `delivery-route-order-${selectionId}`
  const itemsPanelId = `${checkboxId}-items`
  const orderLabel = getDeliveryRouteOrderLabel(order)
  const isManualDelivery = order?.source_type === 'manual'
  const primaryLabel = order.recipient_name || orderLabel
  const orderItems = getDeliveryRouteOrderItems(order)
  const orderItemCount = getDeliveryRouteOrderItemCount(orderItems)
  const orderItemLabel = orderItemCount === 1 ? 'Item' : 'Items'
  const hasOrderItems = orderItems.length > 0
  const isPastDue = isSelectableDeliveryRouteOrderPastDue(order)
  const isChecked = isLocked || isSelected
  const rowClassName = cn(
    'relative rounded-md border bg-card p-3 shadow-none transition-colors duration-150',
    isLocked && 'border-border bg-muted text-muted-foreground ring-1 ring-border',
    !isSelected &&
      !isLocked &&
      !isAddressBlocked &&
      isPastDue &&
      'border-status-destructive-border bg-status-destructive-background ring-1 ring-status-destructive-border hover:border-status-destructive-foreground',
    isSelected &&
      !isLocked &&
      'border-selection-border bg-accentSecondary ring-2 ring-selection-border/30',
    isAddressBlocked &&
      'border-status-destructive-border bg-status-destructive-background ring-1 ring-status-destructive-border',
    !isSelected &&
      !isLocked &&
      !isSelectionDisabled &&
      !isPastDue &&
      'border-border/80 hover:border-primary/30 hover:bg-background',
    isDisabledForLocation && !isLocked && 'border-border/60 bg-muted/55 shadow-none',
    isSelectionDisabled && !isChecked && 'text-muted-foreground',
    isMapHighlighted &&
      'z-10 ring-2 ring-action ring-offset-2 ring-offset-background motion-safe:animate-[delivery-route-card-attention_850ms_ease-out_infinite]',
    isManualDelivery && onRemoveManualDelivery && !isLocked && !isReadOnly && 'pb-11',
    isManualDelivery
      ? 'cursor-default'
      : isSelectionDisabled
        ? 'cursor-not-allowed'
        : 'cursor-pointer'
  )
  const metadataClassName = cn(
    'flex flex-wrap items-center gap-1.5',
    isSelectionDisabled && 'text-muted-foreground'
  )
  const handleCardClick = () => {
    if (isManualDelivery || isSelectionDisabled) {
      return
    }

    onToggle?.(selectionId, !isSelected)
  }
  const stopCardToggle = (event) => {
    event.stopPropagation()
  }
  const handleItemsToggle = (event) => {
    event.stopPropagation()
    setIsItemsOpen((currentValue) => !currentValue)
  }
  const handleCardMouseEnter = () => {
    onHoverChange?.(selectionId, true)
  }
  const handleCardMouseLeave = () => {
    onHoverChange?.(selectionId, false)
  }

  useEffect(() => {
    if (!scrollIntoViewOnMapHighlight) {
      return
    }

    cardRef.current?.scrollIntoView?.({
      behavior: 'smooth',
      block: 'center',
      inline: 'nearest',
    })
  }, [scrollIntoViewOnMapHighlight])

  return (
    <div
      ref={cardRef}
      className={rowClassName}
      data-testid="delivery-route-order-card"
      data-order-id={selectionId}
      title={locationMismatchDescription ?? undefined}
      onClick={handleCardClick}
      onMouseEnter={handleCardMouseEnter}
      onMouseLeave={handleCardMouseLeave}
    >
      {isManualDelivery ? (
        <div className="absolute right-3 top-3 z-10 flex items-center gap-1">
          <Badge variant="secondary" size="xs" className="whitespace-nowrap">
            Custom stop
          </Badge>
          {onEditManualDelivery && !isLocked && !isReadOnly ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-6"
              aria-label={`Edit custom stop ${primaryLabel}`}
              onClick={(event) => {
                stopCardToggle(event)
                onEditManualDelivery(order)
              }}
            >
              <Pencil className="size-3.5" />
            </Button>
          ) : null}
        </div>
      ) : (
        <DeliveryRouteOrderLink
          order={order}
          aria-label={orderLabel}
          title={orderLabel}
          className="absolute right-3 top-3 z-10 whitespace-nowrap text-xs font-medium text-muted-foreground hover:text-interaction"
          onClick={stopCardToggle}
        >
          #{order.id}
        </DeliveryRouteOrderLink>
      )}

      <div className="min-w-0 flex items-start gap-2">
        {!isManualDelivery ? (
          <Checkbox
            id={checkboxId}
            className="mt-0.5"
            aria-label={
              isReadOnly
                ? `${getDeliveryRouteOrderLabel(order)} cannot be changed in read-only mode`
                : isLocked
                  ? `${getDeliveryRouteOrderLabel(order)} is locked on this route`
                  : `Select ${getDeliveryRouteOrderLabel(order)} for this route`
            }
            checked={isChecked}
            disabled={isSelectionDisabled}
            onClick={stopCardToggle}
            onCheckedChange={(checked) => {
              if (isSelectionDisabled) {
                return
              }

              onToggle?.(selectionId, Boolean(checked))
            }}
          />
        ) : null}

        <div className="min-w-0 flex-1 space-y-2">
          <div className="space-y-1.5">
            <div className="flex flex-wrap items-center gap-1.5 pr-12">
              <Typography
                variant="body"
                tone={isSelectionDisabled ? 'muted' : 'default'}
                weight="semibold"
                className="block break-words"
              >
                {primaryLabel}
              </Typography>
              {routeStopNumber ? (
                <Badge variant="outline" size="xs">
                  Stop {routeStopNumber}
                </Badge>
              ) : null}
              {isLocked ? (
                <Badge variant="muted" size="xs" className="py-1 pr-2 pl-1">
                  <Lock />
                  Locked
                </Badge>
              ) : null}
              {isLocked && order.is_delivered ? (
                <Badge variant="success" size="xs">
                  Delivered
                </Badge>
              ) : null}
              {order.is_route_missed ? (
                <Badge variant="warning" size="xs">
                  Missed
                </Badge>
              ) : null}
              {!isChecked && isSelectionDisabled ? (
                <Badge variant="muted" size="xs">
                  Unavailable
                </Badge>
              ) : null}
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              <DeliveryAddressVerificationBadge
                deliveryAddress={order.delivery_address}
                tooltipSide="bottom"
              />
              <Typography
                variant="caption"
                tone={isSelectionDisabled ? 'muted' : 'default'}
                className="block break-words"
              >
                {getDeliveryRouteOrderAddress(order)}
              </Typography>
            </div>
          </div>

          <div className={metadataClassName}>
            {isPastDue ? (
              <Badge variant="destructive" size="xs">
                Past due
              </Badge>
            ) : null}
            <Badge variant="outline" size="xs">
              {formatDeliveryRouteDate(order.fulfillment_due_date)}
            </Badge>
            <Typography variant="caption">
              {getDeliveryRouteLocationLabel(order.location)}
            </Typography>
            {hasOrderItems ? (
              <Button
                variant="ghostMuted"
                size="mini"
                aria-expanded={isItemsOpen}
                aria-controls={itemsPanelId}
                onClick={handleItemsToggle}
              >
                <ShoppingBasket className="size-3.5" />
                {orderItemLabel} ({orderItemCount})
                <ChevronDown
                  className={cn('size-3 transition-transform', isItemsOpen && 'rotate-180')}
                />
              </Button>
            ) : null}
            {isDisabledForLocation ? (
              <Badge
                variant="muted"
                size="xs"
                className="px-1.5"
                title={locationMismatchDescription ?? undefined}
              >
                Different location
              </Badge>
            ) : null}
          </div>

          {hasOrderItems && isItemsOpen ? (
            <DeliveryRouteOrderItemsList
              order={order}
              id={itemsPanelId}
              className="space-y-2 border-t border-border/70 pt-2"
              onClick={stopCardToggle}
            />
          ) : null}

          {isAddressBlocked ? (
            <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-destructive/20 bg-background/80 px-2 py-1.5">
              <Badge variant="destructive" size="xs">
                Address needs verification
              </Badge>
              <Typography variant="caption">{blockedDescription}</Typography>
              {!isManualDelivery ? (
                <DeliveryRouteOrderLink
                  order={order}
                  searchParams={salesOrderFixAddressSearch}
                  className="text-xs font-medium text-destructive hover:text-destructive"
                  onClick={stopCardToggle}
                >
                  Fix address
                </DeliveryRouteOrderLink>
              ) : null}
            </div>
          ) : null}

          {locationMismatchDescription ? (
            <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-border/70 bg-background/80 px-2 py-1.5">
              <Badge variant="muted" size="xs">
                Different location
              </Badge>
              <Typography variant="caption">{locationMismatchDescription}</Typography>
            </div>
          ) : null}
        </div>
      </div>

      {isManualDelivery && onRemoveManualDelivery && !isLocked && !isReadOnly ? (
        <Button
          type="button"
          variant="ghostDestructive"
          size="icon-2xs"
          className="absolute right-3 bottom-3"
          aria-label={`Remove custom stop ${primaryLabel}`}
          onClick={(event) => {
            stopCardToggle(event)
            onRemoveManualDelivery(order)
          }}
        >
          <Trash2 className="size-4" />
        </Button>
      ) : null}
    </div>
  )
}

export default SelectableDeliveryRouteOrderRow
